<template>
  <JoszakiPage
    :page-name="pageName"
    class="InspirationLandingPage"
    inner-class="flex flex-col min-w-0 gap-4"
  >
    <BreadCrumbs :links="breadCrumbs" />
    <InspirationListPageHeader :title="title" />

    <div ref="inputContainer" class="flex flex-col md:w-80 self-center">
      <JoszakiInput
        :placeholder="$t('inspirations.searchPlaceholder')"
        :value="keyword"
        icon-left="search"
        enterkeyhint="search"
        @input="handleSearch"
      />
    </div>

    <div class="flex flex-col gap-4 py-4">
      <template v-if="fetchState.pending">
        <InspirationListItemPlaceholder v-for="i in 15" :key="i" />
      </template>
      <span v-else-if="fetchState.error" class="text-error">
        {{ $t("inspirations.loadError") }}
      </span>
      <template v-else-if="!!inspirations?.length">
        <template v-for="(inspiration, index) in inspirations">
          <InspirationListItem :key="index" :inspiration="inspiration" />
          <BlockTender
            v-if="index % 6 === 2"
            :key="`tender-${index}`"
            text-input
            :title="$t('inspirations.blockTenderTitle')"
            :sub-title="$t('inspirations.blockTenderSubTitle')"
            @requestTender="
              requestTender({
                source: `${pageName}-${index}`,
                description: $event,
              })
            "
          />
        </template>
      </template>
      <template v-else>
        <JoszakiEmptyMessage>
          <p>
            {{ $t("inspirations.noMatchingInspirations") }}
          </p>
        </JoszakiEmptyMessage>
        <JoszakiButton :to="localePath({ name: 'inspirationLandingPage' })">
          {{ $t("inspirations.letsSeeInspirations") }}
        </JoszakiButton>
      </template>
    </div>
    <PaginationComponent
      v-if="pagination"
      :pagination-info="pagination"
      class="mt-8"
    />
  </JoszakiPage>
</template>

<script>
import {
  useContext,
  useFetch,
  ref,
  useRoute,
  useRouter,
  watch,
  computed,
  nextTick,
} from "@nuxtjs/composition-api";
import debounce from "lodash/debounce";
import InspirationListItem from "./InspirationListItem.vue";
import InspirationListItemPlaceholder from "./InspirationListItemPlaceholder.vue";
import InspirationListPageHeader from "./InspirationListPageHeader.vue";
import { useRequestTender } from "~/composables/requestTender";
import JoszakiPage from "~/components/_refactored/common/JoszakiPage.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import JoszakiEmptyMessage from "~/components/_refactored/common/JoszakiEmptyMessage.vue";
import INSPIRATIONS from "~/components/_refactored/inspirationPage/inspirations.graphql";
import INSPIRATION_SSR from "~/components/_refactored/inspirationPage/inspirationSsr.graphql";

export default {
  name: "InspirationListPage",
  components: {
    JoszakiPage,
    BlockTender,
    BreadCrumbs,
    PaginationComponent,
    InspirationListItem,
    InspirationListItemPlaceholder,
    InspirationListPageHeader,
    JoszakiEmptyMessage,
  },
  props: {
    professionTaskSeoName: {
      type: String,
      default: null,
    },
    professionSeoName: {
      type: String,
      default: null,
    },
    pageName: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const { $query, i18n, localePath, $helpers, $trackers } = useContext();
    const router = useRouter();
    const route = useRoute();

    const currentPage = computed(() => Number(route.value.query.page) || 1);
    const keyword = computed(() => route.value.query.keyword ?? "");
    const inspirations = ref(null);
    const pagination = ref(null);
    const profession = ref(null);
    const professionTask = ref(null);
    const inputContainer = ref(null);

    const { fetch, fetchState } = useFetch(async () => {
      const { inspirations: response } = await $query(INSPIRATIONS, {
        page: currentPage.value,
        pageSize: 15,
        professionSeoName: props.professionSeoName,
        professionTaskSeoName: props.professionTaskSeoName,
        keyword: keyword.value,
      });
      inspirations.value = response.items.map((inspiration) => ({
        ...inspiration,
        url: localePath({
          name: "inspirationPage",
          params: {
            inspirationSeoName: inspiration.seoName,
          },
        }),
        photoSrc: inspiration.firstPhoto
          ? $helpers.getReferencePhoto(inspiration.firstPhoto.name)
          : "",
      }));
      pagination.value = response.pagination;
    });

    useFetch(async () => {
      const response = await $query(INSPIRATION_SSR, {
        professionSeoName: props.professionSeoName,
        professionTaskSeoName: props.professionTaskSeoName,
      });
      profession.value = response.professionBySeoName;
      professionTask.value = response.professionTaskBySeoName;
    });

    const handleSearch = debounce((input) => {
      router.push({
        query: {
          ...route.value.query,
          keyword: input,
          page: 1,
        },
      });
      $trackers.trackEvent({
        title: "Search Inspirations",
        data: {
          keyword: input,
        },
      });
    }, 500);

    watch([currentPage, keyword], () => {
      fetch();
      nextTick(() => {
        if (inputContainer.value) {
          inputContainer.value.scrollIntoView({ behavior: "smooth" });
        }
      });
    });

    const breadCrumbs = computed(() => {
      const links = [
        {
          name: i18n.t("inspirations.pageTitle"),
          page: localePath({ name: "inspirationLandingPage" }),
        },
      ];

      if (profession.value) {
        links.push({
          name: profession.value.name,
          page: localePath({
            name: "inspirationProfessionPage",
            params: { professionSeoName: profession.value.seoName },
          }),
        });
      }

      if (professionTask.value) {
        links.push({
          name: professionTask.value.name,
          page: localePath({
            name: "inspirationProfessionTaskPage",
            params: { professionTaskSeoName: professionTask.value.seoName },
          }),
        });
      }

      return links;
    });

    const title = computed(() => {
      const name = profession.value?.name ?? professionTask.value?.name;

      if (name) {
        const nameCapitalized = $helpers.capitalize(name);

        return `${nameCapitalized} ${i18n.t("inspirations.pageTitle")}`.trim();
      }

      return i18n.t("inspirations.mainPageTitle");
    });

    return {
      breadCrumbs,
      fetchState,
      inputContainer,
      currentPage,
      inspirations,
      pagination,
      profession,
      professionTask,
      keyword,
      title,
      handleSearch,
      ...useRequestTender(),
    };
  },
  head() {
    return {
      title: this.title,
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: this.title,
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
        },
        {
          hid: "og:image",
          property: "og:image",
          content: "/joszaki-head.png",
        },
        {
          hid: "description",
          name: "description",
          content: this.$t("inspirations.landingDescription"),
        },
        {
          hid: "og:description",
          property: "og:description",
          content: this.$t("inspirations.landingDescription"),
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ],
    };
  },
};
</script>
